/**
 * SirCAD document model.
 *
 * This is a JSON-canonical model, but its shape is dictated by the CIRCAD v6
 * primitive list (see OG.h, "ID bytes for list elements"). Every field here
 * exists because the original stores it; nothing is invented for convenience.
 * That is what makes import/export lossless in practice.
 *
 * Units
 * -----
 * All lengths are **inches**, stored as doubles, exactly as CIRCAD does
 * internally (its Units:: record is a display/parse table, not a storage unit).
 * All angles are **degrees**. Callers convert for display only.
 */

// The fabrication limits a document may carry. Its own module because they
// belong to whoever is making the board rather than to the drawing.
import type { DfmRules } from './dfm/rules.ts';

// ---------------------------------------------------------------------------
// Layers
// ---------------------------------------------------------------------------

/**
 * Layer type. Values match `tabtype[]` in OGdlg07.cpp:448 and are the low 3
 * bits of the layer control element's `pridt`. Bit 3 (value 8) is the
 * top/bottom flag, modelled separately as `bottom`.
 *
 * The type — not the name or index — is what drives CAM behaviour: which
 * layers netscan treats as connected copper, which get mask swell, which get
 * mirrored on output, and which are ignored entirely.
 */
export const LayerType = {
  Silk: 0,
  Mask: 1,
  Copper: 2,
  Plane: 3,
  Symbols: 4,
  Paste: 5,
  Schematic: 6,
  Drawing: 7,
} as const;

export type LayerType = (typeof LayerType)[keyof typeof LayerType];

export const LAYER_TYPE_NAMES: Record<LayerType, string> = {
  [LayerType.Silk]: 'Silk',
  [LayerType.Mask]: 'Mask',
  [LayerType.Copper]: 'Copper',
  [LayerType.Plane]: 'Plane',
  [LayerType.Symbols]: 'Symbols',
  [LayerType.Paste]: 'Paste',
  [LayerType.Schematic]: 'Schematic',
  [LayerType.Drawing]: 'Drawing',
};

/** Layers that carry electrical connectivity — netscan and the router use these. */
export function isElectrical(t: LayerType): boolean {
  return t === LayerType.Copper || t === LayerType.Plane;
}

export interface Layer {
  /** Layer number = index in the document's layer array. */
  num: number;
  name: string;
  type: LayerType;
  /** Bottom-side layer (bit 3 of the stored type nibble). */
  bottom: boolean;
  /**
   * Buried in the stack: copper you cannot see because the board is on top of
   * it.
   *
   * CIRCAD cannot say this. Its layer nibble has room for a type and one bit
   * for "bottom", so an inner copper layer is stored as a top-side copper
   * layer and nothing distinguishes it from the outer one. That is harmless
   * when every layer is drawn in its own colour at once, which is all the
   * original ever did with them, and wrong the moment anything tries to show
   * the board as it would actually look: the realistic views were painting
   * every mid layer and every plane onto the top face.
   *
   * It changes what is *shown*, never what is *made* — an inner layer is a
   * real layer and still gets its own film.
   */
  inner?: boolean;
  /** Screen colour, 0xRRGGBB. */
  screen: number;
  /** Print colour, 0xRRGGBB. */
  print: number;
  /**
   * CIRCAD writes colours with a leading `+` or `-`. The sign is the "negative"
   * / knockout flag (OG7.cpp:2804), not part of the colour value.
   */
  screenNegative: boolean;
  printNegative: boolean;
  /** Editor state — not part of the CIRCAD file, defaulted on load. */
  visible: boolean;
  locked: boolean;
  /**
   * Explicit net this layer carries. CIRCAD infers a plane's net from the
   * Flood Fill HotSignal; we let it be assigned per layer, so a copper or
   * plane layer can name the net it is poured with.
   */
  net?: string;
}

// ---------------------------------------------------------------------------
// Pads
// ---------------------------------------------------------------------------

/**
 * Pad type, written by OG7.cpp:2300 as a letter (T = through, S = surface)
 * plus a shape digit. The full set is enumerated in OGdlg00.cpp:2249.
 */
export type PadType = 'T0' | 'T1' | 'T2' | 'T3' | 'S0' | 'S1' | 'S2' | 'S3';

export const PAD_TYPE_LABELS: Record<PadType, string> = {
  T0: 'Schematic Pin',
  T1: 'Oval thru Pad',
  T2: 'Rectangular thru Pad',
  T3: 'Round thru Via',
  S0: 'Padstack special',
  S1: 'Oval SMT Pad',
  S2: 'Rectangular SMT Pad',
  S3: 'Schematic Dot',
};

/** Through-hole pad types may carry a drill; surface types never do. */
export function padIsThrough(t: PadType): boolean {
  return t.charAt(0) === 'T';
}

/** Rectangular pad shapes (digit 2). Digit 1 is oval, 0/3 are round. */
export function padIsRect(t: PadType): boolean {
  return t.charAt(1) === '2';
}

/** Pads that exist only in schematics and produce no copper. */
export function padIsSchematic(t: PadType): boolean {
  return t === 'T0' || t === 'S3';
}

/**
 * The pad type a via is made with on a board.
 *
 * Never `T0`, whatever the file says. `T0` is the schematic pin type and
 * `padIsSchematic` excludes it from copper *everywhere* — connectivity, the
 * clearance check, the pour, the Gerber plot and the Excellon program all skip
 * it by name. A via stamped `T0` is therefore a hole nobody drills, on a
 * layer change nothing joins: the router reports the connection made, the
 * ratsnest keeps drawing it, Net Scan calls it an open, and the fabricated
 * board has two unconnected tracks where the route was. Every via the router
 * placed was stamped that way.
 */
export function viaType(doc: CadDocument): PadType {
  const t = doc.defaults.via?.type;
  return t && !padIsSchematic(t) ? t : 'T3';
}

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export interface EntityBase {
  id: number;
  /** Layer number. Control/default records may omit it. */
  layer: number;
  /** Explicit colour override, 0xRRGGBB. Absent means "use the layer colour". */
  color?: number;
  /**
   * Signal (net) name. CIRCAD attaches this to the *geometry*, which is why
   * its netscan, DRC and ratsnest are all one mechanism. We keep that.
   */
  signal?: string;
  /** Free-text `Data:` payload. */
  data?: string;
  /**
   * Laid by the router rather than by hand.
   *
   * The original marks its own routes with a `Route:` sub-element so that
   * "delete previous auto-routes" has something to find, and re-running the
   * router does not have to choose between wiping a person's work and leaving
   * its own. Same purpose here.
   */
  auto?: boolean;
  /** `Origin:` — component-relative origin index, high nibble of pridt. */
  origin?: number;
}

/** A polyline vertex. `bulge` is the arc bulge factor for the segment ending here. */
export interface Vertex {
  x: number;
  y: number;
}

/**
 * `Line::` — a two-point segment, optionally bulged into an arc.
 * This is the workhorse primitive: traces, silk, board outline segments.
 */
export interface LineEntity extends EntityBase {
  kind: 'line';
  width: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Bulge angle in degrees; 0/absent = straight. */
  bulge?: number;
  /** Line style index (low nibble of pridt); 0 = solid. */
  style?: number;
  /** `Cost:` — autorouter cost weighting. */
  cost?: number;
}

/**
 * `Fill::` — a polyline / filled region with an arbitrary vertex list.
 * The writer chunks vertices into `FillMore::` continuation records every 256
 * points (OG7.cpp:2112); the parser rejoins them.
 */
export interface FillEntity extends EntityBase {
  kind: 'fill';
  width: number;
  /** Bounding box as written, `Bounds:x1,y1;x2,y2`. */
  bounds: { x1: number; y1: number; x2: number; y2: number };
  vertices: Vertex[];
  style?: number;
  /**
   * When present, this fill is a poured plane rather than a plain solid: it is
   * drawn filled and then cleared around any geometry not on its `signal`, at
   * the given clearances. Produced by the Flood Fill command.
   */
  flood?: {
    toPad: number;
    toLine: number;
    /**
     * How a pad on the pour's own net joins it.
     *
     * `solid` floods right up to the pad, which is electrically ideal and
     * makes the joint very hard to solder — the plane carries the heat away
     * faster than an iron delivers it. `thermal` clears the pad like any other
     * and ties it back with four spokes. Absent means solid, which is what
     * pours made before this existed did.
     */
    connect?: 'solid' | 'thermal';
    /** Width of each thermal spoke. */
    spokeWidth?: number;
    /**
     * Poured copper that turned out to reach nothing, to be taken back out.
     *
     * Clearances can cut a piece of the pour off from the rest of it, leaving
     * a lump joined to no net at all — copper that floats, picks up noise and
     * gives solder somewhere to bridge. Worked out when the pour is made
     * rather than every time it is drawn, because it costs a grid walk and the
     * answer only changes when the board does. Re-pour after editing, which is
     * what re-pouring has always been for.
     */
    islands?: { x1: number; y1: number; x2: number; y2: number }[];
    /**
     * The thermal spokes that actually reach poured copper.
     *
     * Worked out with the islands, from the same walk, and for the same
     * reason: whether an arm joins anything depends on what its neighbours
     * cleared, which is not knowable from the pad alone. Drawn from here
     * rather than recomputed, so the screen and the plot cannot differ about
     * which arms exist.
     */
    spokes?: {
      x1: number; y1: number; x2: number; y2: number; width: number;
      /** The pad the arm serves, so unconnected pads can be counted. */
      cx: number; cy: number;
    }[];
  };
}

/** `Arc::` — arc, circle or ellipse (via eccentricity). */
export interface ArcEntity extends EntityBase {
  kind: 'arc';
  width: number;
  cx: number;
  cy: number;
  radius: number;
  /** Start angle, degrees CCW from +X. */
  angle: number;
  /** Sweep, degrees. Zero sweep means a full circle. */
  sweep: number;
  /** Ellipse minor/major ratio; 0 or 1 = circular. */
  eccentricity?: number;
  /** Rotation of the ellipse axis, degrees. */
  rotation?: number;
  /** Arc sub-type (low nibble of pridt). */
  type?: number;
}

/** `Spiral::` — spiral of Archimedes. Rare, but round-trips. */
export interface SpiralEntity extends EntityBase {
  kind: 'spiral';
  width: number;
  cx: number;
  cy: number;
  r1: number;
  r2: number;
  a1: number;
  a2: number;
}

/** `Pad::` — pad, hole, via, or schematic pin. */
/**
 * What a pad is actually shaped like.
 *
 * CIRCAD knows three shapes; every board drawn in the last twenty years uses
 * more. Rather than force an imported pad into the nearest of the three and
 * lose the difference for good, the real shape is kept here and the reduction
 * to CIRCAD's vocabulary happens once, when a `.PCB` is written — where the
 * true shape also rides along in the record's free-text payload, so our own
 * files round-trip exactly while the original still opens them.
 *
 * `sizeX` and `sizeY` remain the pad's overall extent in every case, so a
 * consumer that only wants a bounding box need not look at this at all.
 */
export type PadShape =
  | { kind: 'round' }
  | { kind: 'rect' }
  /** A stadium: semicircular ends on the long axis. */
  | { kind: 'obround' }
  /** A rectangle with rounded corners — the commonest surface pad there is. */
  | { kind: 'roundrect'; radius: number }
  /** A rectangle with its corners cut off square. */
  | { kind: 'chamfered'; chamfer: number }
  /** `delta` widens one end and narrows the other, as KiCad's trapezoid does. */
  | { kind: 'trapezoid'; delta: number }
  /** Anything else, as an outline about the pad's centre. */
  | { kind: 'polygon'; outline: Vertex[] };

export interface PadEntity extends EntityBase {
  kind: 'pad';
  /**
   * The CIRCAD type code.
   *
   * Retained because it is what the v6 format stores and what every existing
   * file and library part carries. It conflates two separate questions —
   * whether the pad is drilled, and what shape it is — which is why both now
   * have fields of their own. Read those through {@link padThrough} and
   * {@link padShape}, which fall back to this when they are absent.
   */
  type: PadType;
  /**
   * Whether the pad is drilled through the board.
   *
   * Absent means "ask the type code", which is how every file written before
   * this existed behaves.
   */
  through?: boolean;
  /** The pad's true shape. Absent means "ask the type code". */
  shape?: PadShape;
  x: number;
  y: number;
  /** Pad size. `sizeY` differs from `sizeX` only for non-square pads. */
  sizeX: number;
  sizeY: number;
  /** Drill diameter; only meaningful for through types. */
  hole?: number;
  /**
   * One end of a routed slot, when the hole is not round.
   *
   * `hole` is then the slot's width and the pad sits at the slot's midpoint,
   * where its copper is; this is one end of the routed path and the other
   * follows by symmetry, which is what makes a single point enough. CIRCAD has
   * no such thing — every hole it knows is round — but a board imported from
   * elsewhere may well have one, and a slot has to reach the drill file.
   * Putting it anywhere else is not a cosmetic error: a slot written among the
   * board outline is routed as a cut in the profile, and the board comes back
   * with a bite out of its edge.
   */
  slot?: { x: number; y: number };
  /** Rotation, degrees. */
  rotation?: number;
  /** Pin name/number within the parent component. */
  pin?: string;
  /**
   * The designer says this pin joins nothing, and means it.
   *
   * A schematic marks a deliberately idle pin — Altium's No-ERC cross, KiCad's
   * X — and that mark is the difference between a pin the drawing forgot and a
   * pin the drawing dismissed. Without it connectivity reports both the same
   * way: of 98 pins touching no wire across the sample Altium sheets, 90 carry
   * the mark, so a plain unconnected count was wrong by an order of magnitude
   * and buried the eight that were real.
   */
  noConnect?: boolean;
  /**
   * Which sides the solder mask covers rather than opens.
   *
   * A designer decides whether the mask exposes a via or covers it. Covered
   * over the annular ring is *tenting*; covered right across the hole is a
   * masked or filled via. CIRCAD has no such setting — it assumes every via is
   * open and derives each mask opening from the pad — so this is ours, and
   * absent means open, which is what every existing file means.
   *
   * It matters because the decision is not recoverable from the copper. A
   * board imported from KiCad and plotted again opened mask over all 1050 of
   * its pads where the original opens 793, and a fabricator's checker returned
   * 520 solder-mask failures for the webs that left between them.
   */
  tented?: 'top' | 'bottom' | 'both';
  /**
   * No stencil aperture here, however ordinary the pad looks.
   *
   * Which pads are pasted is a decision, not a consequence: a test point, a
   * solder bridge, a connector shell or a pad meant for hand soldering is left
   * out of the stencil deliberately. Deriving paste from the pads cannot know
   * that, and the paste film is the only record of it — the same problem as
   * tenting, and carried the same way.
   */
  noPaste?: boolean;
  /**
   * Mask expansion for this pad alone, in inches, overriding the board rule.
   *
   * CIRCAD has one swell for the whole board. KiCad states a margin per
   * footprint and per pad, so a board taken in from it does not have a single
   * figure; using one left every opening slightly wrong.
   */
  maskMargin?: number;
  /**
   * Stencil aperture change for this pad alone, in inches, overriding the
   * board rule. Negative makes the aperture smaller than the pad, which is
   * the usual direction.
   *
   * A stencil is not one inset applied everywhere. A fine-pitch part wants
   * proportionally less paste than a large one, and the two sides of a board
   * populated differently want different amounts — measured across one board
   * here, a single figure left the top at 0.94 of the original and the bottom
   * at 1.25.
   */
  pasteMargin?: number;
}

/** Text anchor points, `txap[]` in the original. */
export type TextAnchor =
  | 'BL' | 'BC' | 'BR'
  | 'ML' | 'MC' | 'MR'
  | 'TL' | 'TC' | 'TR';

/** `Text::` — stroked text. */
export interface TextEntity extends EntityBase {
  kind: 'text';
  x: number;
  y: number;
  /** Character height. */
  height: number;
  /** Total string length; the original stores it and uses it for fitting. */
  length?: number;
  /** Multi-line count. */
  lines?: number;
  anchor?: TextAnchor;
  /**
   * The original CIRCAD `Anchor:` code, verbatim. CIRCAD's anchor is a 4×4 grid
   * (16 codes, including an "origin"/baseline row and column) — finer than
   * {@link TextAnchor}'s nine points — so this preserves the exact code for a
   * byte-faithful save. Rendering uses {@link anchor}; the writer emits this
   * when it still agrees with `anchor`, else falls back to `anchor`.
   */
  anchorRaw?: string;
  /**
   * The original CIRCAD `Rotation:` code, verbatim. CIRCAD reads a bare angle
   * <=1 as a fraction of a turn, so `Rotation:1` is a full turn (0°); preserving
   * the string keeps a load→save from rewriting it as `1°`, a real 1° tilt.
   */
  rotationRaw?: string;
  font?: string | number;
  /** Style bits (bold/italic). */
  style?: number;
  /** Width/height ratio; 1 = natural. */
  aspect?: number;
  /** Arc radius for text on a curve. */
  arc?: number;
  rotation?: number;
  /**
   * Printed mirrored — lettering on the underside of a board.
   *
   * CIRCAD has no word for this: its text reads the way it is written, and
   * only a mirrored *component* turns the strings inside it. Enough for a
   * board drawn in CIRCAD, where underside legends live in mirrored parts;
   * not enough for one imported from elsewhere. Altium marks each string
   * itself, and free lettering on the underside — a web address, a copyright
   * line — belongs to no part at all, so there is nowhere else for it to live.
   *
   * Absent means what every file written before this meant: not mirrored.
   */
  mirrored?: boolean;
  /** The string itself. Lives in `Data:`. */
  text: string;
}

/**
 * `Component::` … `EndComp::` — a placed part.
 *
 * `params` holds P1..P9. These are the original's BOM fields, and they carry
 * template markers: the stock library ships `P1:"C?"` / `P2:"?µF"`, where `?`
 * is filled in at placement time. `refPrefix`/`value` are conveniences derived
 * from P1/P2, not separate storage.
 */
export interface ComponentEntity extends EntityBase {
  kind: 'component';
  name: string;
  /** Flags word from `Flags:`. */
  flags?: number;
  bounds: { x1: number; y1: number; x2: number; y2: number };
  /** Placement point from `EndComp:: InsertionPoint:`. */
  x: number;
  y: number;
  /** Rotation in degrees. Stored in the file as a fraction of a turn. */
  rotation?: number;
  mirrored?: boolean;
  /**
   * Placement may not move this part.
   *
   * A connector, a mounting hole, a switch that has to line up with a hole in
   * the case: where these go is decided by the enclosure and not by the
   * netlist. CIRCAD has no such flag — every part there is moved by hand, so
   * nothing needed to be told not to — and the quadratic seed needs them
   * anyway, because a placement with nothing fixed has no anchor and the whole
   * cloud collapses to a point.
   */
  fixed?: boolean;
  /** P1..P9, sparse. Index 1..9. */
  params: Record<number, string>;
  /**
   * PCB footprint this symbol maps to, e.g. `DIP14`.
   *
   * The compact libraries carry it as the trailing field of a symbol's spec
   * line, and the original emits it as the netlist's "Target pattern" column.
   * It is not a P-parameter, so it gets its own field rather than being
   * smuggled into one.
   */
  footprint?: string;
  /**
   * How many gates the physical package holds — six for a 7404.
   *
   * The library encodes this as the reference template's trailing letter
   * (`U?f`), but placing a gate replaces that letter with the gate's own
   * (`U?a`), so the package size has to be carried separately or annotation
   * cannot tell how many gates may share one chip.
   */
  gates?: number;
  /** The component's own geometry, in component-local coordinates. */
  children: Entity[];
}

export type Entity =
  | LineEntity
  | FillEntity
  | ArcEntity
  | SpiralEntity
  | PadEntity
  | TextEntity
  | ComponentEntity;

export type EntityKind = Entity['kind'];

// ---------------------------------------------------------------------------
// Document-level settings — one interface per CIRCAD control record
// ---------------------------------------------------------------------------

/** `Units::` — display unit table. Storage is always inches. */
export interface UnitsSetting {
  /** `Default:` — the primary display unit, in inches. */
  default: number;
  /** `Table:` — alternate units, e.g. 25400µm, 25.4mm, 2.54cm, 0.0254m. */
  table: { scale: number; name: string }[];
}

/** `Gridsnaps::` */
export interface GridSetting {
  current: number;
  index: number;
  table: number[];
}

/** `NetScan::` — the DRC clearance rules. */
export interface NetScanSetting {
  lineToLine: number;
  lineToPad: number;
  padToPad: number;
  ratLine: number;
}

/** `FloodFill::` — copper pour parameters. */
export interface FloodFillSetting {
  floodToLine: number;
  floodToPad: number;
  floodLine: number;
  hatchGrid: number;
  hotSignal?: string;
  coldSignal?: string;
  targetLayer: number;
  sourceLayers: number[];
}

/** `GerberOut::` — the swell/shrink values applied during CAM output. */
export interface GerberOutSetting {
  maskPadSwell: number;
  stencilPadShrink: number;
  planePadSwell: number;
  planeHoleSwell: number;
  floodLineWidth: number;
}

/** `GerberFile::` — one output file definition. */
export interface GerberFileSetting {
  sequence: number;
  type: number;
  suffix: string;
  layers: number[];
}

/** `AutoRoute::` */
export interface AutoRouteSetting {
  algorithms: number;
  layers: number[];
}

/** `Print::` — plot setup carried on the document. */
export interface PrintSetting {
  flags: number[];
  scaling: number;
  bounds: { x1: number; y1: number; x2: number; y2: number };
  transparencyColor?: number;
  highlightColor?: number;
}

/** `Display::` */
export interface DisplaySetting {
  layer: number | string;
  drawModes: number;
  dotSpacing: number;
  dotColor: number;
  backgroundColor: number;
  highlightColor: number;
  attentionColor: number;
}

/**
 * The layer a `number | string` reference names, or undefined for one that
 * names nothing here.
 *
 * The original writes a layer either as an index or as its name in quotes, and
 * both spellings ship in its own libraries — `Display:: Layer:0` in PCB.LIB,
 * `Display:: Layer:"Master"` in SCH.LIB. Undefined rather than zero when the
 * name is not found, so a caller can fall back deliberately instead of landing
 * on the first layer in the stack by accident.
 */
export function layerNumber(
  doc: CadDocument, ref: number | string | undefined,
): number | undefined {
  if (typeof ref === 'number') return doc.layers[ref] ? ref : undefined;
  if (typeof ref !== 'string') return undefined;
  const want = ref.trim().toLowerCase();
  const at = doc.layers.findIndex((l) => l.name.trim().toLowerCase() === want);
  return at >= 0 ? at : undefined;
}

/** Default primitive parameters — `DefaultLine::`, `DefaultPad::`, etc. */
export interface Defaults {
  line?: { layer?: number | string; width: number; style?: number };
  fill?: { layer?: number | string; width: number; style?: number };
  arc?: { layer?: number | string };
  pad?: { type: PadType; size: number; hole?: number; pin?: string };
  text?: { layer?: number | string; height: number };
  via?: { layer?: number | string; type: PadType; size: number; hole?: number };
  /**
   * The dimension-text default. Files spell the record either
   * `DefaultXYDimension::` or `PlaceXYDimension::`, so the spelling that was
   * read is kept — writing the other one back renames a record the original
   * put there, which a load-and-save should never do.
   */
  xyDimension?: { size: number; keyword?: string };
}

export type DocumentKind = 'pcb' | 'sch';

/**
 * A SirCAD document. One of these per schematic sheet or per board.
 *
 * `source` preserves the header of an imported CIRCAD file so a round-trip
 * regenerates a recognisable, diff-friendly file rather than a synthetic one.
 */
/**
 * A photograph placed under the drawing, to trace over.
 *
 * The point of it is reverse engineering: photograph a board, get the picture
 * square and to scale, and lay tracks and parts on top of what you can see.
 *
 * The picture itself is *not* part of the drawing. Only where it goes is —
 * which is how the format has always had it: the record carries an insertion
 * point, a resolution and a file name, and the pixels stay in the file they
 * came from. A phone photograph is several megabytes, it is scaffolding rather
 * than design data, and nobody sent a board file expecting a holiday snap
 * inside it.
 *
 * Kept out of `entities` deliberately. A photograph must never reach a Gerber,
 * a netlist, a drill file or an isolation path, and the surest way to
 * guarantee that is for the code that walks the drawing never to see one.
 */
export interface ImageRef {
  /** The file it came from, as recorded. The pixels are loaded per session. */
  name: string;
  /** Insertion point, in inches. */
  x: number;
  y: number;
  /** Size of the picture in pixels, as recorded when it was placed. */
  width: number;
  height: number;
  /**
   * Inches per pixel — what sets the picture's size on the board.
   *
   * Scaling a photograph is the whole difficulty of tracing from one: get this
   * wrong and every track you lay is wrong by the same factor.
   */
  resolution: number;
  /**
   * Inches per pixel vertically, when it differs from the horizontal.
   *
   * A photograph taken at any angle but straight down is foreshortened, and a
   * scanner or a phone lens can stretch one axis on its own. Rather than force
   * a square-on photograph nobody has, the two axes can be set apart. Absent —
   * which is the normal case — the picture is square and {@link resolution}
   * governs both.
   */
  resolutionY?: number;
  rotation?: number;
  layer?: number;
  /** Where the insertion point sits on the picture. */
  anchor?: TextAnchor;
}
export interface CadDocument {
  /**
   * Which version of this model the document was written against.
   *
   * Not the CIRCAD file's version — that is {@link CadDocument.source}, and it
   * describes the program a document came *from*. This one describes the shape
   * of the document itself, so a build that has since changed the model knows
   * what it is looking at.
   *
   * Absent means a document written before the field existed: version 0. Every
   * row already in storage is one, which is why the migration from 0 is the
   * one that had to work first. See `native/migrate.ts`.
   */
  formatVersion?: number;
  kind: DocumentKind;
  name: string;
  units: UnitsSetting;
  zeroPoint: { x: number; y: number };
  grid: GridSetting;
  layers: Layer[];
  display?: DisplaySetting;
  print?: PrintSetting;
  netScan?: NetScanSetting;
  /**
   * What the board house can make, when this file has been told.
   *
   * Optional, and absent means the standard profile — the same fall-through
   * `netScan` uses. An absent setting leaves a document incomplete rather than
   * wrong, which is why adding it needs no migration: every older document
   * reads exactly as it always did. See `dfm/rules.ts`.
   */
  dfm?: DfmRules;
  floodFill?: FloodFillSetting;
  gerberOut?: GerberOutSetting;
  gerberFiles?: GerberFileSetting[];
  autoRoute?: AutoRouteSetting;
  defaults: Defaults;
  entities: Entity[];
  /** Photographs placed under the drawing to trace from; referenced, not embedded. */
  images?: ImageRef[];
  /** Records we parsed but do not yet model, kept verbatim for round-tripping. */
  unknown?: string[];
  source?: {
    version: string;
    program: string;
    fileName?: string;
    fileDate?: string;
    fileTime?: string;
  };
}

/**
 * A component library. CIRCAD stores these in exactly the same format as a
 * design file — a document whose entities are all top-level components — which
 * is why the same parser handles both.
 */
export interface Library {
  name: string;
  kind: DocumentKind;
  /** Name-substitution rules from the library header, e.g. `74LS*=74*`. */
  aliases: { pattern: string; target: string }[];
  document: CadDocument;
}

// ---------------------------------------------------------------------------
// Construction helpers
// ---------------------------------------------------------------------------

let nextId = 1;
export function newId(): number {
  return nextId++;
}
/** Reset the id counter — used when loading a document wholesale. */
export function seedIds(from: number): void {
  nextId = Math.max(nextId, from + 1);
}

export const DEFAULT_UNITS: UnitsSetting = {
  default: 1.0,
  table: [
    { scale: 25400, name: 'µm' },
    { scale: 25.4, name: 'mm' },
    { scale: 2.54, name: 'cm' },
    { scale: 0.0254, name: 'm' },
  ],
};

/**
 * The stock PCB layer stack, copied from the header of the shipped PCB.LIB so
 * a new board matches what the original program would have given you.
 */
export function defaultPcbLayers(): Layer[] {
  const L = (
    num: number, name: string, type: LayerType, bottom: boolean,
    screen: number, screenNegative: boolean, inner = false,
  ): Layer => ({
    num, name, type, bottom, inner,
    screen, print: 0x000000,
    screenNegative, printNegative: true,
    visible: true, locked: false,
  });
  return [
    L(0, 'Pad Master', LayerType.Silk, false, 0xaa00aa, false),
    L(1, 'Symbols', LayerType.Symbols, false, 0xffff00, false),
    L(2, 'CAM Control', LayerType.Drawing, false, 0x000000, true),
    L(3, 'Top Silk', LayerType.Silk, false, 0x000000, false),
    L(4, 'Bottom Silk', LayerType.Silk, true, 0x00aa00, true),
    L(5, 'Top Mask', LayerType.Mask, false, 0x00aa00, false),
    L(6, 'Bottom Mask', LayerType.Mask, true, 0x000000, true),
    L(7, 'Top Copper', LayerType.Copper, false, 0xaa0000, false),
    L(8, 'Bottom Copper', LayerType.Copper, true, 0x0000aa, false),
    // The planes and the mid layers are buried — a power plane is what sits
    // between the outer copper, and a mid layer is by definition in the middle.
    L(9, 'VCC', LayerType.Plane, false, 0xff5555, false, true),
    L(10, 'GND', LayerType.Plane, false, 0x5555ff, false, true),
    L(11, 'Mid Layer 1', LayerType.Copper, false, 0x000000, true, true),
    L(12, 'Mid Layer 2', LayerType.Copper, false, 0x000000, true, true),
    L(13, 'Mid Layer 3', LayerType.Copper, false, 0x000000, true, true),
    L(14, 'Mid Layer 4', LayerType.Copper, false, 0x000000, true, true),
    L(15, 'Ratnest', LayerType.Drawing, false, 0xffff00, false),
  ];
}

/** The stock schematic layer stack, from SCH.LIB. */
export function defaultSchLayers(): Layer[] {
  const L = (
    num: number, name: string, type: LayerType, screen: number,
  ): Layer => ({
    num, name, type, bottom: false,
    screen, print: 0xffff00,
    screenNegative: false, printNegative: false,
    visible: true, locked: false,
  });
  return [
    L(0, 'Master', LayerType.Silk, 0x00aa00),
    L(1, 'Symbols', LayerType.Symbols, 0x000000),
    L(2, 'Signals', LayerType.Schematic, 0x0000aa),
    L(3, 'Busses', LayerType.Symbols, 0xaa0000),
    L(4, 'Pin names', LayerType.Symbols, 0x555555),
    L(5, 'Pin numbers', LayerType.Symbols, 0x000000),
  ];
}

/**
 * Stock display settings, copied from the headers of the shipped PCB.LIB and
 * SCH.LIB.
 *
 * The background is mid-grey, not black. That is not a stylistic leftover: the
 * stock layer palette puts silk and symbols on **black** and uses saturated
 * primaries for copper, which only works against a light-mid ground. Switching
 * to a dark canvas would make every symbol invisible and force a repaint of
 * all twenty libraries' colours.
 */
export function defaultDisplay(kind: DocumentKind): DisplaySetting {
  return {
    layer: kind === 'sch' ? 'Master' : 0,
    drawModes: kind === 'sch' ? 0x00030203 : 0x00030603,
    dotSpacing: kind === 'sch' ? 0.1 : 0.25,
    dotColor: 0xffffff,
    backgroundColor: 0x909090,
    highlightColor: 0xffffff,
    attentionColor: 0xffff00,
  };
}

/**
 * The model version this build writes.
 *
 * Raise it in the same commit that changes the model, and add the migration
 * that gets a document from the version before to this one. The two belong
 * together: a bump with no migration is a document nobody can open, and a
 * migration with no bump is one that never runs.
 *
 * 1 — entity ids are unique within a document.
 */
export const FORMAT_VERSION = 2;

export function emptyDocument(kind: DocumentKind, name = 'Untitled'): CadDocument {
  return {
    // A document this build built is by definition current. That covers the
    // importers too, which all come through here — measured across the 88
    // sample designs, none of them produces the defect version 1 repairs.
    formatVersion: FORMAT_VERSION,
    kind,
    name,
    display: defaultDisplay(kind),
    units: structuredClone(DEFAULT_UNITS),
    zeroPoint: { x: 0, y: 0 },
    grid: kind === 'sch'
      ? { current: 0.1, index: 1, table: [0.01, 0.1] }
      : { current: 0.025, index: 3, table: [0.001, 0.005, 0.025] },
    layers: kind === 'sch' ? defaultSchLayers() : defaultPcbLayers(),
    // The schematic's four are SCH.LIB's own `DefaultLine::`/`DefaultPad::`/
    // `DefaultText::`/`DefaultVia::` records, verbatim.
    //
    // PCB.LIB carries none of them, so the board's had to come from elsewhere.
    // The line width is the original's own fallback, written when the default
    // record has no width: `fladt(es,IDB_LINE_WIDTH,3,0.01)` (OGmenu02.cpp:76
    // and :127, Place Line and Place Ortho-Line). It was .001" — a one-mil
    // hairline you can barely see, and ten times too thin. The via is exactly
    // what all forty of the sample boards carry, none of them having changed
    // it. The pad and text are the commonest values across those same boards.
    defaults: kind === 'sch'
      ? {
          line: { width: 0.01, style: 0 },
          pad: { type: 'T0', size: 0.01 },
          text: { height: 0.06 },
          via: { type: 'S3', size: 0.04 },
        }
      : {
          line: { width: 0.01, style: 0 },
          pad: { type: 'T1', size: 0.1, hole: 0.06 },
          text: { height: 0.06 },
          via: { type: 'T3', size: 0.05, hole: 0.028 },
        },
    entities: [],
  };
}
